import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ArrowBack, MonetizationOn, CheckCircle, InfoOutlined, PendingActions, LocalShipping } from "@mui/icons-material";
import api from "../../api/axios";

/* ── Helpers ── */
function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function getStatusLabel(status) {
  if (!status) return "In Progress";
  const s = status.toUpperCase();
  if (s === "NONE") return "Accepted";
  if (s === "PICKED_UP") return "Picked Up";
  if (s === "IN_TRANSIT") return "In Transit";
  if (s === "DELIVERED") return "Delivered";
  return status;
}

const CARD_ACCENTS = {
  totalCommission: { icon: "#d97706", bg: "rgba(217,119,6,0.10)", border: "#fef3c7" },
  totalOrders: { icon: "#16a34a", bg: "rgba(22,163,74,0.10)", border: "#dcfce7" },
  pendingCommission: { icon: "#0284c7", bg: "rgba(2,132,199,0.10)", border: "#e0f2fe" },
  pendingOrders: { icon: "#7c3aed", bg: "rgba(124,58,237,0.10)", border: "#ede9fe" },
};

const LIMIT = 20;

export default function AgentCommissionDetail() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchPage = useCallback(async (pageNum) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
      const { data } = await api.get(`/agent/orders/accepted?${params}`);
      const newOrders = data.orders || [];

      setAllOrders((prev) => pageNum === 1 ? newOrders : [...prev, ...newOrders]);
      setPage(pageNum);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error("Failed to fetch commission orders:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    fetchPage(page + 1);
  }, [page, fetchPage]);

  const hasMore = allOrders.length < totalCount;

  const deliveredOrders = useMemo(() =>
    allOrders.filter(o => o.deliveryPhase?.status === "DELIVERED"),
  [allOrders]);

  const totals = useMemo(() => {
    let commission = 0;
    deliveredOrders.forEach(o => {
      const c = o.agentCommission?.amount || 0;
      commission += Number(c);
    });
    return { commission, count: deliveredOrders.length };
  }, [deliveredOrders]);

  const pendingTotals = useMemo(() => {
    let commission = 0;
    let count = 0;
    allOrders.forEach(o => {
      if (o.deliveryPhase?.status !== "DELIVERED") {
        const c = o.agentCommission?.amount || 0;
        commission += Number(c);
        count++;
      }
    });
    return { commission, count };
  }, [allOrders]);

  const kpiCards = useMemo(() => [
    { id: "totalCommission", title: "Total Commission Earned", value: formatCurrency(totals.commission) },
    { id: "totalOrders", title: "Delivered Orders", value: totals.count },
    { id: "pendingCommission", title: "Commission to Be Earned", value: formatCurrency(pendingTotals.commission) },
    { id: "pendingOrders", title: "Orders to Deliver", value: pendingTotals.count },
  ], [totals, pendingTotals]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/agent/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} />
          Back to Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>💰 Commission Details</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>Breakdown of commissions earned from delivered orders</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        {/* KPI Widgets */}
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, alignItems: "stretch", mb: 3 }}>
          {kpiCards.map((c) => {
            const accent = CARD_ACCENTS[c.id] || CARD_ACCENTS.totalCommission;
            return (
              <Box key={c.id} sx={{ p: 1.25, borderRadius: "12px", bgcolor: "#fff", border: `1.5px solid ${accent.border}`, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, width: "100%", boxSizing: "border-box", minHeight: { xs: 80, sm: 80, md: 90 } }}>
                <Box sx={{ width: { xs: 34, sm: 38, lg: 36 }, height: { xs: 34, sm: 38, lg: 36 }, borderRadius: "10px", bgcolor: accent.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {c.id === "totalCommission" ? <MonetizationOn sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} /> :
                   c.id === "totalOrders" ? <CheckCircle sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} /> :
                   c.id === "pendingCommission" ? <PendingActions sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} /> :
                   <LocalShipping sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", lg: "0.68rem" }, fontWeight: 500, color: "#64748b", lineHeight: 1.2, mb: 0.4, overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</Typography>
                  {loading ? (
                    <Box sx={{ width: "70%", height: 24, bgcolor: "#e2e8f0", borderRadius: 1 }} />
                  ) : (
                    <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem", lg: "1rem" }, fontWeight: 700, lineHeight: 1, color: "#0f172a" }}>{c.value}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Info note */}
        {!loading && allOrders.length > 0 && (
          <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: "rgba(217,119,6,0.06)", border: "1px solid #fef3c7", display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlined sx={{ fontSize: 16, color: "#d97706", flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "#92400e", fontSize: "0.72rem" }}>
              Commission is calculated only for <strong>Delivered</strong> orders. "Accepted" = order accepted but not yet picked up. "NONE" status means the delivery phase hasn't started.
            </Typography>
          </Paper>
        )}

        {/* Details Table */}
        <Paper sx={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
              <MonetizationOn sx={{ fontSize: 18, color: "#d97706" }} />
              Commission History ({allOrders.length} of {totalCount} orders loaded)
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={32} sx={{ color: "#d97706", mb: 1.5 }} /><Typography variant="body2" sx={{ color: "#94a3b8" }}>Loading commission data...</Typography></Box>
          ) : allOrders.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(217,119,6,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}><MonetizationOn sx={{ fontSize: 32, color: "#d97706" }} /></Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1e293b", mb: 0.5 }}>No commission earned yet</Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>Complete deliveries to start earning commissions.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Order Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Commission</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b" }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allOrders.map((order, idx) => {
                      const isDelivered = order.deliveryPhase?.status === "DELIVERED";
                      const commission = order.agentCommission?.amount || 0;
                      return (
                        <TableRow key={`${order._id}-${idx}`} sx={{ "&:hover": { bgcolor: "#f8fafc" }, cursor: "pointer", opacity: isDelivered ? 1 : 0.55 }} onClick={() => navigate(`/agent/orders/${order._id}`)}>
                          <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1976d2", fontSize: "0.78rem" }}>#{order._id?.substring(0, 8).toUpperCase()}</Typography></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{formatCurrency(order.total)}</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} sx={{ color: isDelivered ? "#16a34a" : "#94a3b8", fontSize: "0.78rem" }}>
                              {formatCurrency(commission)}
                            </Typography>
                          </TableCell>
                          <TableCell><Chip label={getStatusLabel(order.deliveryPhase?.status)} size="small" sx={{ fontWeight: 600, fontSize: "0.65rem", height: 22, bgcolor: isDelivered ? "rgba(22,163,74,0.12)" : "rgba(100,116,139,0.10)", color: isDelivered ? "#16a34a" : "#64748b" }} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 4 }}>
                    {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}